/**
 * PasswordGate — the shared centered-card password screen every research view locks behind.
 *
 * Deliberately NOT an auth boundary: it is a deterrent that keeps an in-progress research
 * surface off a stray click. Relay role and organization checks remain the access boundary.
 *
 * Four views (G3, G4, G9, StreamRift) previously carried a byte-identical copy of this card
 * plus its own `sha256`; the only things that ever differed were the title, the subtitle,
 * the expected hash, and which storage key held the unlocked bit — which is exactly this
 * component's props.
 */

import { useCallback, useState } from 'react';
import { type GateNamespace, recordUnlock, sha256 } from '@/lib/infra/gateStorage';

interface PasswordGateProps {
  /** Large heading — the surface's name, e.g. "G3". */
  title: string;
  /** One-line description under the title, e.g. "Research & Development". */
  subtitle: string;
  /** Where this gate's unlocked bit is persisted. */
  namespace: GateNamespace;
  /** Lowercase-hex SHA-256 of the accepted password. The cleartext stays out of the bundle. */
  expectedHash: string;
  /** Called once the password matches, after the unlock is persisted. */
  onUnlock: () => void;
}

export function PasswordGate({
  title,
  subtitle,
  namespace,
  expectedHash,
  onUnlock,
}: PasswordGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!password.trim()) return;

      setVerifying(true);
      setError(null);

      try {
        const hash = await sha256(password);
        if (hash === expectedHash) {
          recordUnlock(namespace, { hash });
          onUnlock();
        } else {
          setError('Incorrect password');
          setPassword('');
        }
      } catch {
        setError('Verification failed');
      } finally {
        setVerifying(false);
      }
    },
    [password, expectedHash, namespace, onUnlock],
  );

  return (
    <div className="flex h-full items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-foreground text-xl font-bold">{title}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>
        </div>

        <div className="bg-card border-border rounded-lg border p-6">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoFocus
                disabled={verifying}
                className="bg-background border-border text-foreground focus:border-primary w-full rounded border px-3 py-2 text-sm focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={verifying || !password.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 mt-3 w-full rounded px-3 py-2 text-sm font-medium disabled:opacity-50"
            >
              {verifying ? 'Verifying...' : 'Enter'}
            </button>
          </form>
        </div>

        {error && (
          <div className="bg-destructive/10 border-destructive/20 mt-3 rounded border p-2">
            <p className="text-destructive text-center text-xs">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
